import React, { createContext, useContext, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { auth, onAuthStateChanged } from '../supabaseAuth';
import { syncCollection, fetchAllData } from '../supabaseDB';
import { mergeById, activeRecords } from '../syncMerge';
import { getPeriodRange, inPeriod } from '../periods';
import { useBusiness } from './BusinessContext';
import { fetchAllBusinessData } from '../services/queries';
import { adaptAllBusinessData } from '../services/dataAdapter';
import { runLegacyMigration } from '../services/legacyMigration';

const AppContext = createContext(null);

// ── localStorage helpers ───────────────────────────────────────────
const getKey = (uid, name) => uid ? `faida_${uid}_${name}` : `faida_${name}`;

const load = (uid, name) => {
  try {
    const raw = localStorage.getItem(getKey(uid, name));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const save = (uid, name, value) => {
  try {
    localStorage.setItem(getKey(uid, name), JSON.stringify(value));
  } catch {}
};

// ── Void registry ─────────────────────────────────────────────────
// Separate localStorage key that tracks voided record IDs per collection.
// This CANNOT be overwritten by cloud sync merge, so voids always survive.
const VOID_REGISTRY_KEY = (uid) => uid ? `faida_${uid}_voidRegistry` : `faida_voidRegistry`;

const loadVoidRegistry = (uid) => {
  try {
    return JSON.parse(localStorage.getItem(VOID_REGISTRY_KEY(uid)) || '{}');
  } catch { return {}; }
};

const saveVoidRegistry = (uid, registry) => {
  try {
    localStorage.setItem(VOID_REGISTRY_KEY(uid), JSON.stringify(registry));
  } catch {}
};

// Register a void: adds {collection: [id, ...]} entry.
//
// IDENTITY DISCONTINUITY (root cause of the "50K resurrection"):
// A locally-created record carries id = Date.now() and _rid = relational UUID.
// After a refresh the SAME record arrives from the relational adapter with
// id = the relational UUID and no _rid at all. A registry keyed on only one of
// those two values therefore stops matching across the very refresh boundary it
// exists to protect. So we record EVERY identity the record is known by, and
// applyVoidRegistry matches on any of them.
const registerVoid = (uid, collection, ...ids) => {
  const reg = loadVoidRegistry(uid);
  if (!reg[collection]) reg[collection] = [];
  ids.forEach((id) => {
    if (id === null || id === undefined || id === '') return;
    const sid = String(id);
    if (!reg[collection].includes(sid)) reg[collection].push(sid);
  });
  saveVoidRegistry(uid, reg);
};

// Re-apply voided status from registry onto a records array.
// Matches on the record's current id OR its relational _rid, so a void survives
// the local-id -> relational-UUID transition that happens on refresh.
const applyVoidRegistry = (uid, collection, records) => {
  const reg = loadVoidRegistry(uid);
  const voidedIds = new Set(reg[collection] || []);
  if (voidedIds.size === 0) return records;
  return records.map(r => {
    if (!r) return r;
    const matches =
      voidedIds.has(String(r.id)) ||
      (r._rid != null && voidedIds.has(String(r._rid)));
    if (!matches) return r;

    // Incoming copy ALREADY says void -> the source of truth agrees with the
    // registry, so any earlier "not synced" marker is stale and must be cleared.
    // (Without this the flag was written once and never re-evaluated, so the
    // NOT SYNCED badge stuck forever even after the server caught up.)
    if (r.status === 'void') {
      if (r._voidUnsynced) {
        const { _voidUnsynced, ...healed } = r;
        return healed;
      }
      return r;
    }

    // A record the user never actually posted was never a real transaction, so
    // the server having it as 'draft' is not a void-sync failure. Re-stamp it
    // locally (it must still contribute zero) but do NOT raise the alarm.
    const neverPosted = r.status === 'draft';
    if (!neverPosted) {
      // The authoritative store still said this record was active while the
      // registry says the user voided it. Surface the divergence so a failed
      // void RPC is diagnosable instead of silent.
      console.warn(
        `[VOID] registry re-applied void to ${collection} id=${r.id}` +
        `${r._rid ? ` rid=${r._rid}` : ''} — the server copy was NOT void. ` +
        `The void RPC for this record did not succeed.`
      );
    }
    return {
      ...r,
      status: 'void',
      voidedAt: r.voidedAt || new Date().toISOString(),
      updatedAt: r.updatedAt || new Date().toISOString(),
      ...(neverPosted ? {} : { _voidUnsynced: true }),
    };
  });
};

// Collections that are arrays (vs objects/settings)
const COLLECTIONS = [
  'products', 'sales', 'purchases', 'expenses',
  'expenseCategories', 'suppliers', 'customers', 'manualTransactions',
  'serviceTransactions',
];

// ── AppProvider ────────────────────────────────────────────────────
export const AppProvider = ({ children }) => {
  const { businessId, isOwner } = useBusiness();
  const [uid, setUid]   = useState(null);
  const tokenRef        = useRef(null);   // Supabase access token (for REST calls)
  const [cloudSynced, setCloudSynced] = useState(false); // true once first cloud fetch done
  const [isOnline, setIsOnline]       = useState(navigator.onLine);
  const [relationalLoaded, setRelationalLoaded] = useState(false);

  // ── Core data arrays ───────────────────────────────────────────
  const [products,           setProducts]           = useState([]);
  const [sales,              setSales]              = useState([]);
  const [purchases,          setPurchases]          = useState([]);
  const [expenses,           setExpenses]           = useState([]);
  const [expenseCategories,  setExpenseCategories]  = useState([]);
  const [suppliers,          setSuppliers]          = useState([]);
  const [customers,          setCustomers]          = useState([]);
  const [manualTransactions,  setManualTransactions]  = useState([]);
  const [serviceTransactions, setServiceTransactions] = useState([]);

  // Map collection name → setter (used for bulk load from cloud)
  const setters = useRef({});
  useEffect(() => {
    setters.current = {
      products:            setProducts,
      sales:               setSales,
      purchases:           setPurchases,
      expenses:            setExpenses,
      expenseCategories:   setExpenseCategories,
      suppliers:           setSuppliers,
      customers:           setCustomers,
      manualTransactions:  setManualTransactions,
      serviceTransactions: setServiceTransactions,
    };
  });

  // ── Online/offline tracking ────────────────────────────────────
  useEffect(() => {
    const up   = () => setIsOnline(true);
    const down = () => setIsOnline(false);
    window.addEventListener('online',  up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);

  // ── Debounced cloud sync ───────────────────────────────────────
  // Each collection gets its own debounce timer so one noisy array
  // doesn't delay saves for others.
  const syncTimers = useRef({});
  // RACE GUARD: don't push state UP to cloud until the initial cloud fetch
  // has completed. Otherwise an empty initial array (new device, slow network)
  // can overwrite real cloud data after the 2-second debounce fires.
  const cloudLoadedRef = useRef(false);

  const queueSync = useCallback((name, data) => {
    if (!tokenRef.current) return;
    if (!cloudLoadedRef.current) return; // wait for cloud load before pushing up
    clearTimeout(syncTimers.current[name]);
    syncTimers.current[name] = setTimeout(() => {
      syncCollection(uid, tokenRef.current, name, data);
    }, 2000); // 2-second debounce
  }, [uid]);

  // ── Load from cloud on login, then fall back to localStorage ──
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userId = user.uid;
        const token  = user.accessToken;
        setUid(userId);
        tokenRef.current = token;

        // 1. Load local data first so app is instantly usable.
        // The void registry is applied here too, so the very first paint after a
        // refresh can never show a voided record as active (all three load paths
        // -local, cloud JSONB, relational -now agree on void status).
        const localData = {};
        COLLECTIONS.forEach(name => {
          const data = applyVoidRegistry(userId, name, load(userId, name));
          localData[name] = data;
          setters.current[name]?.(data);
        });

        // 2. Fetch cloud data in background
        try {
          const cloudData = await fetchAllData(userId, token);

          if (cloudData && Object.keys(cloudData).length > 0) {
            // Cloud has data — use it as source of truth
            COLLECTIONS.forEach(name => {
              const cloud = cloudData[name];
              if (Array.isArray(cloud)) {
                // Tombstone-safe merge (C3): merge cloud + local by id, newest
                // stamp wins, and a deletion beats an older live copy — so a
                // record deleted on another device is NOT resurrected by this
                // device's stale local copy. Tombstones are retained in the
                // merged array so re-sync keeps the deletion.
                const local = localData[name] || [];
                const merged = applyVoidRegistry(userId, name, mergeById(cloud, local));
                setters.current[name]?.(merged);
                save(userId, name, merged);
                // If the merge changed anything vs the cloud (local-only records
                // OR local tombstones the cloud didn't have), push it back up.
                const changedVsCloud =
                  merged.length !== cloud.length ||
                  merged.some((r) => {
                    const c = cloud.find((x) => x && r && x.id === r.id);
                    return !c || !!c.deleted !== !!r.deleted;
                  });
                if (changedVsCloud) {
                  syncCollection(userId, token, name, merged);
                }
              }
            });
          } else {
            // No cloud data yet — upload existing local data (first-time migration)
            const hasLocalData = COLLECTIONS.some(name => (localData[name] || []).length > 0);
            if (hasLocalData) {
              COLLECTIONS.forEach(name => {
                if ((localData[name] || []).length > 0) {
                  syncCollection(userId, token, name, localData[name]);
                }
              });
              console.log('[FaidaDB] First-time migration: local data uploaded to cloud');
            }
          }
        } catch (e) {
          console.warn('[FaidaDB] Cloud load failed, using local data:', e);
        }

        cloudLoadedRef.current = true; // safe to push UP to cloud from now on
        setCloudSynced(true);
      } else {
        // Logged out — clear everything
        setUid(null);
        tokenRef.current = null;
        cloudLoadedRef.current = false; // re-gate until next login + cloud fetch
        setCloudSynced(false);
        COLLECTIONS.forEach(name => setters.current[name]?.([]));
      }
    });
    return unsubscribe;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Relational DB read (Phase 8 production path) ───────────────
  // When businessId is available (from BusinessContext), fetch data from
  // the normalized relational schema via SECURITY DEFINER query functions.
  //
  // When switching businesses, all collections are cleared first so stale
  // data from the previous business doesn't leak through. Relational data
  // is always authoritative for the current business.
  const jsonbLoadedRef = useRef(false);
  const prevBusinessIdRef = useRef(null);

  useEffect(() => {
    if (!businessId || !tokenRef.current || !cloudLoadedRef.current) return;
    let cancelled = false;

    // Detect business switch — clear all collections so stale data doesn't show
    const isBizSwitch = prevBusinessIdRef.current && prevBusinessIdRef.current !== businessId;
    prevBusinessIdRef.current = businessId;

    const RELATIONAL_SETTERS = {
      products: setProducts,
      sales: setSales,
      purchases: setPurchases,
      expenses: setExpenses,
      expenseCategories: setExpenseCategories,
      suppliers: setSuppliers,
      customers: setCustomers,
      manualTransactions: setManualTransactions,
      serviceTransactions: setServiceTransactions,
    };

    if (isBizSwitch) {
      // Clear all collections immediately so the UI doesn't show old business data
      Object.values(RELATIONAL_SETTERS).forEach(setter => setter([]));
      setRelationalLoaded(false);
    }

    (async () => {
      try {
        const raw = await fetchAllBusinessData(businessId, tokenRef.current);
        if (cancelled || !raw) return;

        const adapted = adaptAllBusinessData(raw);
        if (!adapted) return;

        let anyData = false;
        Object.entries(RELATIONAL_SETTERS).forEach(([key, setter]) => {
          const relational = adapted[key];
          if (!Array.isArray(relational)) return;

          // On business switch, always use relational data (authoritative).
          // On initial load, only overwrite if JSONB is empty (transition safety).
          if (isBizSwitch || relational.length > 0) {
            // Re-apply void registry so locally-voided records are not
            // resurrected by stale relational data (the RPC may have failed
            // on a previous session, leaving the DB row as 'posted').
            const withVoids = uid ? applyVoidRegistry(uid, key, relational) : relational;
            setter(withVoids);
            if (uid) save(uid, key, withVoids);
            anyData = true;
          }
        });

        if (anyData) {
          console.log('[AppContext] Relational data loaded for business:', businessId);
        }
        setRelationalLoaded(true);

        // ── One-time legacy migration (owner only) ──────────────────
        // Pre-Phase-8 data exists only in the owner's localStorage.
        // Migrate unmigrated records to relational DB so team members
        // can see them. Uses stable idempotency keys — safe to re-run.
        if (isOwner && uid && tokenRef.current) {
          const migrationCollections = {
            products: load(uid, 'products'),
            customers: load(uid, 'customers'),
            suppliers: load(uid, 'suppliers'),
            // expenseCategories: handled via direct SQL insert (patch 019)
            sales: load(uid, 'sales'),
            purchases: load(uid, 'purchases'),
            expenses: load(uid, 'expenses'),
          };
          // Check if there are any unmigrated records before running
          const isUUID = (v) => typeof v === 'string' && /^[0-9a-f]{8}-/.test(v);
          const hasUnmigrated = Object.values(migrationCollections).some(arr =>
            (arr || []).some(r => r && !r.deleted && !r._rid && !isUUID(r.id))
          );
          if (hasUnmigrated) {
            // Clear stale flag in case a previous run set it despite errors
            localStorage.removeItem(`faida_migration_done_${uid}_${businessId}`);
            runLegacyMigration(uid, businessId, tokenRef.current, migrationCollections, (collection, oldId, rid) => {
              // Update the in-memory record with _rid so it's not migrated again
              const setter = RELATIONAL_SETTERS[collection];
              if (setter) {
                setter(prev => prev.map(r => r.id === oldId ? { ...r, _rid: rid } : r));
              }
            }).then(result => {
              if (result.migrated > 0) {
                console.log(`[AppContext] Legacy migration: ${result.migrated} records migrated. Reloading relational data...`);
                // Reload relational data to pick up migrated records
                fetchAllBusinessData(businessId, tokenRef.current).then(freshRaw => {
                  if (!freshRaw) return;
                  const freshAdapted = adaptAllBusinessData(freshRaw);
                  if (!freshAdapted) return;
                  Object.entries(RELATIONAL_SETTERS).forEach(([key, setter]) => {
                    const relational = freshAdapted[key];
                    if (Array.isArray(relational) && relational.length > 0) {
                      setter(relational);
                      if (uid) save(uid, key, relational);
                    }
                  });
                }).catch(() => {});
              }
            }).catch(e => console.warn('[AppContext] Legacy migration error:', e.message));
          }
        }
      } catch (err) {
        console.warn('[AppContext] Relational load failed (falling back to JSONB):', err.message);
      }
    })();

    return () => { cancelled = true; };
  }, [businessId, uid, cloudSynced, isOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persist to localStorage + queue cloud sync on every change ─
  useEffect(() => { save(uid, 'products',           products);           queueSync('products',           products);           }, [uid, products,           queueSync]);
  useEffect(() => { save(uid, 'sales',              sales);              queueSync('sales',              sales);              }, [uid, sales,              queueSync]);
  useEffect(() => { save(uid, 'purchases',          purchases);          queueSync('purchases',          purchases);          }, [uid, purchases,          queueSync]);
  useEffect(() => { save(uid, 'expenses',           expenses);           queueSync('expenses',           expenses);           }, [uid, expenses,           queueSync]);
  useEffect(() => { save(uid, 'expenseCategories',  expenseCategories);  queueSync('expenseCategories',  expenseCategories);  }, [uid, expenseCategories,  queueSync]);
  useEffect(() => { save(uid, 'suppliers',          suppliers);          queueSync('suppliers',          suppliers);          }, [uid, suppliers,          queueSync]);
  useEffect(() => { save(uid, 'customers',          customers);          queueSync('customers',          customers);          }, [uid, customers,          queueSync]);
  useEffect(() => { save(uid, 'manualTransactions',  manualTransactions);  queueSync('manualTransactions',  manualTransactions);  }, [uid, manualTransactions,  queueSync]);
  useEffect(() => { save(uid, 'serviceTransactions', serviceTransactions); queueSync('serviceTransactions', serviceTransactions); }, [uid, serviceTransactions, queueSync]);

  // ── Retry pending syncs when coming back online ────────────────
  useEffect(() => {
    if (isOnline && uid && tokenRef.current) {
      COLLECTIONS.forEach(name => {
        const data = load(uid, name);
        if ((data || []).length > 0) {
          syncCollection(uid, tokenRef.current, name, data);
        }
      });
    }
  }, [isOnline, uid]);

  // ── ACTIVE (non-deleted) views ─────────────────────────────────
  // Raw state keeps tombstones (so they persist and sync); everything the app
  // reads and every calculation below uses ONLY the active records. A deleted
  // record therefore cannot affect any total, balance, receivable, payable,
  // dropdown or report. (C3 deletion safety.)
  const activeProducts           = useMemo(() => activeRecords(products),           [products]);
  const activeSales              = useMemo(() => activeRecords(sales),              [sales]);
  const activePurchases          = useMemo(() => activeRecords(purchases),          [purchases]);
  const activeExpenses           = useMemo(() => activeRecords(expenses),           [expenses]);
  const activeSuppliers          = useMemo(() => activeRecords(suppliers),          [suppliers]);
  const activeCustomers          = useMemo(() => activeRecords(customers),          [customers]);
  const activeManualTransactions = useMemo(() => activeRecords(manualTransactions), [manualTransactions]);
  const activeServiceTransactions= useMemo(() => activeRecords(serviceTransactions),[serviceTransactions]);
  const activeExpenseCategories  = useMemo(() => activeRecords(expenseCategories),  [expenseCategories]);

  // ── REMOVED (H1): legacy totals + profit engine ───────────────────────
  // `totalSales / totalPurchases / totalExpenses / totalServiceRevenue` and the
  // old `profit = totalSales + services − purchases − expenses` used to live
  // here. They were unused by any screen AND wrong as an accounting figure
  // (revenue was VAT-inclusive; ALL purchases were treated as COGS). Deleted so
  // they can never be picked up by mistake. Revenue / COGS / gross & net profit
  // now come exclusively from the shared moneyMath helpers + the Business
  // Statement in App.js. VAT comes from moneyMath saleVAT/purchaseVAT.

  // ── LEGACY balance engine — DEV COMPARISON ONLY (H1) ───────────────────
  // This is the OLD transaction-based wallet engine. It is NOT the source of
  // truth and must NOT power any displayed report. It is retained solely so the
  // dev-only reconciliation card (behind the `faida-ledger-check` flag) can show
  // old-vs-new. Real wallet/cash balances come from the cash ledger
  // (cashLedger.js) via App.js `adjustedBalances`. Exported as `legacyBalances`.
  // Known-legacy behaviours (intentionally left, since it's the "before"
  // number): skips partial sales, counts bridge manual transactions, treats
  // services as fully paid, reads mt.account.
  const legacyBalances = useMemo(() => {
    const b = { cash: 0, bank: 0, mobileMoney: 0 };

    activeSales.filter(s => s.status !== 'void').forEach(s => {
      if (s.isPaid === false) return;
      // Split payments: iterate payments array; fall back to paymentMethod for old records
      if (Array.isArray(s.payments) && s.payments.length > 0) {
        s.payments.forEach(pmt => {
          const pmtAmt = parseFloat(pmt.amount || 0);
          // payments are stored with 'type' field (not 'method')
          const pmtType = pmt.type || pmt.method || 'cash';
          if (pmtType === 'bank') b.bank += pmtAmt;
          else if (pmtType === 'mobilemoney') b.mobileMoney += pmtAmt;
          else b.cash += pmtAmt;
        });
      } else {
        const amt = parseFloat(s.totalRevenue || 0);
        if (s.paymentMethod === 'bank') b.bank += amt;
        else if (s.paymentMethod === 'mobilemoney') b.mobileMoney += amt;
        else b.cash += amt;
      }
    });

    activePurchases.filter(p => p.status !== 'void').forEach(p => {
      if (p.isPaid === false) return;
      // Split payments: iterate payments array; fall back to paymentMethod for old records
      if (Array.isArray(p.payments) && p.payments.length > 0) {
        p.payments.forEach(pmt => {
          const pmtAmt = parseFloat(pmt.amount || 0);
          const pmtType = pmt.type || pmt.method || 'cash';
          if (pmtType === 'credit') return; // credit = supplier owes, doesn't affect cash accounts
          if (pmtType === 'bank') b.bank -= pmtAmt;
          else if (pmtType === 'mobilemoney') b.mobileMoney -= pmtAmt;
          else b.cash -= pmtAmt;
        });
      } else {
        const amt = parseFloat(p.totalCost || 0);
        if (p.paymentMethod === 'bank') b.bank -= amt;
        else if (p.paymentMethod === 'mobilemoney') b.mobileMoney -= amt;
        else b.cash -= amt;
      }
    });

    activeExpenses.filter(e => e.status !== 'void').forEach(e => {
      if (e.isPaid === false) return;
      // Split payments: iterate payments array; fall back to paymentMethod for old records
      if (Array.isArray(e.payments) && e.payments.length > 0) {
        e.payments.forEach(pmt => {
          const pmtAmt = parseFloat(pmt.amount || 0);
          const pmtType = pmt.type || pmt.method || 'cash';
          if (pmtType === 'credit') return;
          if (pmtType === 'bank') b.bank -= pmtAmt;
          else if (pmtType === 'mobilemoney') b.mobileMoney -= pmtAmt;
          else b.cash -= pmtAmt;
        });
      } else {
        const amt = parseFloat(e.amount || 0);
        const method = e.paymentMethod || 'cash';
        if (method === 'bank') b.bank -= amt;
        else if (method === 'mobilemoney') b.mobileMoney -= amt;
        else b.cash -= amt;
      }
    });

    // Service transactions (service_only and products_and_services businesses)
    activeServiceTransactions.filter(st => st.status !== 'void').forEach(st => {
      const amt = parseFloat(st.totalRevenue || 0);
      const method = st.paymentMethod || 'cash';
      if (method === 'bank') b.bank += amt;
      else if (method === 'mobilemoney') b.mobileMoney += amt;
      else b.cash += amt;
    });

    activeManualTransactions.forEach(mt => {
      const amt = parseFloat(mt.amount || 0);
      const sign = mt.type === 'credit' ? 1 : -1;
      if (mt.account === 'bank') b.bank += sign * amt;
      else if (mt.account === 'mobilemoney') b.mobileMoney += sign * amt;
      else b.cash += sign * amt;
    });

    return b;
  }, [activeSales, activePurchases, activeExpenses, activeServiceTransactions, activeManualTransactions]);

  // ── Receivables (customers owe) ───────────────────────────────
  // Net amount = totalRevenue − cash actually paid − credit-reduced-by-returns
  // (so partial payments and partial sales-returns properly shrink the receivable)
  const receivables = useMemo(
    () => activeSales.filter(s => s.status !== 'void' && s.isPaid === false).map(s => {
      const total = parseFloat(s.totalRevenue || 0);
      const cashPaid = Array.isArray(s.payments)
        ? s.payments.filter(p => p.type !== 'credit').reduce((a, p) => a + parseFloat(p.amount || 0), 0)
        : 0;
      const reducedByReturns = parseFloat(s.creditReducedByReturns || 0);
      const owed = Math.max(0, total - cashPaid - reducedByReturns);
      return {
        id: s.id,
        customerName: s.customerName || 'Walk-in',
        productName: s.productName,
        amount: owed,
        date: s.date,
      };
    }).filter(r => r.amount > 0.005),
    [activeSales]
  );

  const totalReceivables = useMemo(
    () => receivables.reduce((sum, r) => sum + r.amount, 0),
    [receivables]
  );

  // ── Payables (business owes suppliers) ────────────────────────
  // Net amount = grand total − cash actually paid − payable-reduced-by-returns
  // (mirrors receivables so partial payments and supplier-credit purchase
  // returns properly shrink what we still owe).
  const payables = useMemo(
    () => activePurchases.filter(p => p.status !== 'void' && p.isPaid === false).map(p => {
      const total = parseFloat(p.totalCost || 0);
      const cashPaid = Array.isArray(p.payments)
        ? p.payments.filter(x => x.type !== 'credit').reduce((a, x) => a + parseFloat(x.amount || 0), 0)
        : 0;
      const reducedByReturns = parseFloat(p.payableReducedByReturns || 0);
      const owed = Math.max(0, total - cashPaid - reducedByReturns);
      return {
        id: p.id,
        supplier: p.supplier || '—',
        productName: p.productName,
        amount: owed,
        date: p.date,
      };
    }).filter(r => r.amount > 0.005),
    [activePurchases]
  );

  const totalPayables = useMemo(
    () => payables.reduce((sum, p) => sum + p.amount, 0),
    [payables]
  );

  // ── Period filter helper (H5) ──────────────────────────────────
  // Delegates to the ONE shared range helper so the dashboard KPIs and the
  // Sales/Purchases/Expenses tabs use exactly the same window as the wallet
  // reports and drilldowns. Presets are to-date and exclude future entries;
  // Custom (start/end) can include the future.
  const filterByPeriod = (arr, period, customStart, customEnd) => {
    const range = getPeriodRange(period, customStart, customEnd);
    return (arr || []).filter(item => inPeriod(item && item.date, range));
  };

  return (
    <AppContext.Provider
      value={{
        // Consumers see ONLY active (non-deleted) records; setters operate on
        // the raw state (which keeps tombstones for persistence + sync). All
        // state updates in the app use the `prev =>` functional form, so they
        // receive the raw array and never drop a tombstone.
        products:            activeProducts,            setProducts,
        sales:               activeSales,               setSales,
        purchases:           activePurchases,           setPurchases,
        expenses:            activeExpenses,            setExpenses,
        expenseCategories:   activeExpenseCategories,   setExpenseCategories,
        suppliers:           activeSuppliers,           setSuppliers,
        customers:           activeCustomers,           setCustomers,
        manualTransactions:  activeManualTransactions,  setManualTransactions,
        serviceTransactions: activeServiceTransactions, setServiceTransactions,
        // Receivables / payables ARE the single source for AR/AP (derived from
        // active sales/purchases here; no competing App.js computation).
        receivables,        totalReceivables,
        payables,           totalPayables,
        // DEV-ONLY legacy wallet engine for the reconciliation card. Not used by
        // any displayed report — real balances come from the cash ledger.
        legacyBalances,
        filterByPeriod,
        cloudSynced,
        relationalLoaded,
        isOnline,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);

// Re-export so App.js void handlers can register voids that survive cloud merges
export { registerVoid, applyVoidRegistry, loadVoidRegistry, saveVoidRegistry };
